import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import moment from "moment";
import { Observable, of } from "rxjs";
import { environment } from "src/environments/environment";
import { ChatSessionTranscript } from "../../chats/model";
import { ChartSessionsPartition } from "./models";
import { ChatSummary, SelectedVersions } from "../../chats/state/chat.model";
import {
  DataSourceTypes,
  GroupPathOptions,
} from "../../data-source/state/data-source.model";
import {
  ColorPalleteOptions,
  ColorThemes,
  ColorThemesChat,
  ColorThemesSurvey,
  grayScallePallete,
} from "../../shared/color-themes/color-themes";
import { CustomEncoderService } from "../../shared/custom-encoder.service";
import {
  ChartsFieldsGroup,
  ChatFrequencyGroupParams,
  ChatFrequencyParams,
  ChatFrequencyParamsExt,
  DateTimeFormat,
  GetTranscriptParams,
  GroupedBarResponse,
  ScatterParams,
  ScatterResponse,
  SimpleFrequencyData,
  TimeSeriesCombo,
  TimeSeriesData,
  TranscriptArgs,
} from "../charts.model";
import { SpecificChartType } from "../charts/chart.value-objects";
import { PaletteType } from "../../shared/theme-selector/theme-selector.model";

@Injectable({ providedIn: "root" })
export class ChartsService {
  BASE_CHARTS_URL = `${environment.api_url_eared_dove}/charts`;
  BASE_FREQUENCY_URL = `${this.BASE_CHARTS_URL}/frequency`;

  CHAT_GROUP_FREQUENCY_URL = `${this.BASE_FREQUENCY_URL}/group`;
  CHAT_TIME_SERIES_COMBO = `${this.BASE_CHARTS_URL}/time-series-combo`;
  CHAT_TIME_SERIES_SENTIMENT = `${this.BASE_CHARTS_URL}/time-series`;

  CHAT_CLICK_MESSAGES = `${environment.api_url_eared_dove}/data-source`;

  timeFields = ["elapsed_time", "msg_duration"];

  dateTimeFields = ["msg_date_time", "msg_date", "date"];

  dateFields = ["date"];

  constructor(private http: HttpClient) {}

  getTimeSeries(
    chartParams: ChatFrequencyParamsExt,
    models: SelectedVersions[] = [],
  ): Observable<TimeSeriesData[]> {
    let params = new HttpParams();
    for (const key of Object.keys(chartParams)) {
      params = params.append(key, chartParams[key]);
    }

    return this.http.post<TimeSeriesData[]>(
      `${this.CHAT_TIME_SERIES_SENTIMENT}/${chartParams.data_source_type}/${chartParams.index_id}`,
      models,
      { params },
    );
  }

  getTimeSeriesCombo(
    chartParams: ChatFrequencyParams,
    models: SelectedVersions[] = [],
  ): Observable<TimeSeriesCombo> {
    let params = new HttpParams();
    for (const key of Object.keys(chartParams)) {
      params = params.append(key, chartParams[key]);
    }

    return this.http.post<TimeSeriesCombo>(
      `${this.CHAT_TIME_SERIES_COMBO}/${chartParams.data_source_type}/${chartParams.index_id}`,
      models,
      { params },
    );
  }

  getChartsFields(
    indexId: string,
    dataSourceType: DataSourceTypes,
  ): Observable<ChartsFieldsGroup[]> {
    return this.http.get<ChartsFieldsGroup[]>(
      `${this.BASE_CHARTS_URL}/fields/${dataSourceType}/${indexId}`,
    );
  }

  getFrequencyChat(
    chatFrequencyParams: ChatFrequencyParams,
    models: SelectedVersions[] = [],
  ): Observable<SimpleFrequencyData[]> {
    let params = new HttpParams();

    if (chatFrequencyParams && !chatFrequencyParams.field_name_sub) {
      // Add required params
      params = params.append("field_path_sub", "");
      params = params.append("field_name_sub", "");
    }

    for (const key of Object.keys(chatFrequencyParams)) {
      params = params.append(key, chatFrequencyParams[key]);
    }
    return this.http.post<SimpleFrequencyData[]>(
      `${this.BASE_FREQUENCY_URL}/${chatFrequencyParams.data_source_type}/${chatFrequencyParams.index_id}`,
      models,
      { params },
    );
  }

  getGroupFrequencyChat(
    chatFrequencyGroupParams: ChatFrequencyGroupParams,
    models: SelectedVersions[] = [],
  ): Observable<GroupedBarResponse[]> {
    let params = new HttpParams();
    for (const key of Object.keys(chatFrequencyGroupParams)) {
      params = params.append(key, chatFrequencyGroupParams[key]);
    }
    return this.http.post<GroupedBarResponse[]>(
      `${this.CHAT_GROUP_FREQUENCY_URL}/${chatFrequencyGroupParams.data_source_type}/${chatFrequencyGroupParams.index_id}`,
      models,
      { params },
    );
  }

  sortSeriesByDate(series: any) {
    series.forEach((element) => {
      element.data.sort(function (a, b) {
        return new Date(a[0]).getTime() - new Date(b[0]).getTime();
      });
    });
  }
  /* istanbul ignore next */
  getChatTranscriptByChart(
    transcriptParams: GetTranscriptParams,
    models: SelectedVersions[] = [],
  ): Observable<ChatSessionTranscript[]> {
    let params = new HttpParams();
    for (const key of Object.keys(transcriptParams)) {
      params = params.append(key, transcriptParams[key]);
    }

    params = params.append("chat", transcriptParams.index_id);

    return this.http.post<ChatSessionTranscript[]>(
      `${this.CHAT_CLICK_MESSAGES}/chat/transcript`,
      models,
      { params },
    );
  }

  getScatterPlotData(
    scatterParams: ScatterParams,
    models: SelectedVersions[] = [],
  ): Observable<ScatterResponse[]> {
    let params = new HttpParams();
    for (const key of Object.keys(scatterParams)) {
      params = params.append(key, scatterParams[key]);
    }
    return this.http.post<ScatterResponse[]>(
      `${this.BASE_CHARTS_URL}/scatter-plot/${scatterParams.data_source_type}/${scatterParams.index_id}`,
      models,
      { params },
    );
  }

  dateTimeTransformation(data: any, params: any) {
    const field_type = params.field_type || null;

    if (this.timeFields.includes(params.field_name)) {
      data = data.map((el) => ({
        ...el,
        name: this.formatTime(el.name),
      }));
    }

    if (field_type && this.dateFields.includes(field_type)) {
      data = data.map((el) => {
        if (!el.seriesData) {
          return {
            ...el,
            name: moment
              .unix(el.name / 1000)
              .utc()
              .format(
                DateTimeFormat[params.field_name] || "MM/DD/YYYY h:mm:ss A",
              ),
          };
        } else {
          const formattedSeries = {};
          for (const serie in el.seriesData) {
            if (serie in el.seriesData) {
              formattedSeries[
                moment
                  .unix(Number(serie) / 1000)
                  .utc()
                  .format("MM/DD/YYYY h:mm:ss A")
              ] = el.seriesData[serie];
            }
          }
          return {
            ...el,
            seriesData: formattedSeries,
          };
        }
      });
    }

    if (this.dateFields.includes(params.group_type)) {
      data = data.map((el) => {
        return {
          ...el,
          group: moment
            .unix(el.group / 1000)
            .utc()
            .format(
              DateTimeFormat[params.field_name] || "MM/DD/YYYY h:mm:ss A",
            ),
        };
      });
    }

    return data;
  }

  formatTime(value) {
    const duration: moment.Duration = moment.duration(
      parseInt(value, 10) * 1000,
    );
    return moment.utc(duration.as("milliseconds")).format("HH:mm:ss");
  }

  formatTimeValue(value, fieldName) {
    if (this.timeFields.includes(fieldName)) {
      return this.formatTime(value);
    }

    return value;
  }

  formatTimeSeries(value: number): string {
    return moment
      .unix(value / 1000)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
  }

  formatTranscriptParams(
    value: any,
    fieldName: string,
    group: boolean = false,
  ) {
    if (this.timeFields.includes(fieldName)) {
      const [hh = "0", mm = "0", ss = "0"] = (value || "0:0:0").split(":");
      const hour = parseInt(hh, 10) || 0;
      const minute = parseInt(mm, 10) || 0;
      const second = parseInt(ss, 10) || 0;
      return String(hour * 3600 + minute * 60 + second);
    }

    if (this.dateTimeFields.includes(fieldName)) {
      const formatByField = {
        msg_date_time: "MM/DD/YYYY h:mm:ss A",
        msg_date: "MM/DD/YYYY",
        date: "MM/DD/YYYY",
      };
      const parsed = moment.utc(
        value,
        formatByField[fieldName] || moment.ISO_8601,
        true,
      );

      return (parsed.isValid() ? parsed : moment.utc(value)).format(
        "YYYY-MM-DD HH:mm:ss",
      );
    }

    if (fieldName === "msg_time") {
      const parsed = moment.utc(value, "h:mm:ss A", true);

      return (
        parsed.isValid()
          ? parsed.year(1970).month(0).date(1)
          : moment.utc(`1970-01-01 ${value}`, "YYYY-MM-DD h:mm:ss A", true)
      ).format("YYYY-MM-DD HH:mm:ss");
    }

    return value;
  }

  setSelectedData(params: any, options: any, theme: string[] = null) {
    if (params?.seriesType !== "line") {
      const [series] =
        options.series.length > 1
          ? options.series.filter((el) => el.name === params.seriesName)
          : options.series;

      let grayIndex = Math.floor(Math.random() * grayScallePallete.length - 1);

      series?.data.forEach((data, index) => {
        if (!params.data.isChecked) {
          if (index === params.dataIndex && !data.isChecked) {
            data.isChecked = true;

            if (theme?.length) {
              data.itemStyle = { color: theme[index % theme.length] };
            } else {
              delete data.itemStyle;
            }
          } else {
            grayIndex =
              grayIndex < grayScallePallete.length - 1 ? grayIndex + 1 : 0;
            data.itemStyle = { color: grayScallePallete[grayIndex] };
            data.isChecked = false;
          }
        } else {
          delete data.itemStyle;
          data.isChecked = false;
        }
      });
    }

    return options;
  }

  restoreOptions(options: any) {
    const { series } = options;

    series.forEach((serie) => {
      serie.data.forEach((dt) => {
        if ("itemStyle" in dt) {
          delete dt.itemStyle;
          dt.isChecked = false;
          dt.selected = false;
        }
      });
    });

    return options;
  }

  getTranscript(
    transcriptArgs: TranscriptArgs,
  ): Observable<ChartSessionsPartition> {
    const {
      chartParams,
      echartsInstance,
      chatFrequencyParams,
      models,
      type,
      theme = [],
      current_partition,
      partitions,
    } = transcriptArgs;

    if (
      [SpecificChartType.BarLineCombo, SpecificChartType.Doughnut].includes(
        type,
      )
    ) {
      const options = echartsInstance.getOption();
      echartsInstance.setOption(
        this.setSelectedData(chartParams, options, theme),
      );
    }

    const fieldExt = this.setFieldExt(type, chartParams, chatFrequencyParams);

    const getTranscriptParams: GetTranscriptParams = {
      ...(<GetTranscriptParams>chatFrequencyParams),
      ...fieldExt,
      ...{ current_partition: current_partition },
      ...{ partitions: partitions },
    };

    const selectable = [
      SpecificChartType.BarLineCombo,
      SpecificChartType.Doughnut,
      SpecificChartType.WordCloud,
    ].includes(type);

    // Use Category hash
    const originalName = getTranscriptParams.name;
    if (chartParams.data?.cat_hash)
      getTranscriptParams.name = chartParams.data?.cat_hash
        ? chartParams.data?.cat_hash
        : chartParams.cat_hash;
    else if (chartParams.cat_hash) {
      getTranscriptParams.name = chartParams.cat_hash;
    }

    if (!chartParams.data.isChecked || !selectable) {
      return this.getChatSessions(
        chatFrequencyParams.data_source_type,
        models,
        getTranscriptParams,
      );
    } else {
      return of(new ChartSessionsPartition());
    }
  }

  getChatSessions(
    chart_data_type: string,
    models: SelectedVersions[],
    getTranscriptParams: GetTranscriptParams,
  ): Observable<ChartSessionsPartition> {
    //The Custom Encoder is used to encode the plus "+" sign so that it is handled correctly in the backend
    let params = new HttpParams({ encoder: new CustomEncoderService() });
    for (const key of Object.keys(getTranscriptParams)) {
      params = params.append(key, getTranscriptParams[key]);
    }

    params = params.append(chart_data_type, getTranscriptParams.index_id);

    let endpoint_type = `${chart_data_type}/transcript`;
    if (chart_data_type === DataSourceTypes.SURVEY) {
      endpoint_type = `${chart_data_type}/verbatims`;
    }
    return this.http.post<ChartSessionsPartition>(
      `${this.CHAT_CLICK_MESSAGES}/${endpoint_type}`,
      models,
      { params },
    );
  }

  setFieldExt(type: any, data: any, chatFrequencyParams: any) {
    let group_path = GroupPathOptions.CHAT;
    if (chatFrequencyParams.data_source_type === DataSourceTypes.SURVEY) {
      group_path = GroupPathOptions.SURVEY;
    }
    switch (type) {
      case SpecificChartType.BarLineCombo:
      case SpecificChartType.Doughnut:
      case SpecificChartType.WordCloud:
        return {
          name: this.formatTranscriptParams(
            data["name"],
            chatFrequencyParams.field_name,
          ),
          data_index: data["dataIndex"],
          value: data["value"],
        };
      case SpecificChartType.GroupedBar:
        let fields: any = {
          name: this.formatTranscriptParams(
            data["seriesName"],
            chatFrequencyParams.field_type,
          ),
          data_index: data["dataIndex"],
          field_path_sub: chatFrequencyParams.group_path,
          field_name_sub: chatFrequencyParams.group_name,
        };
        if (data["value"]?.group) {
          fields.group_value = this.formatTranscriptParams(
            data["value"].group,
            chatFrequencyParams.group_type,
            true,
          );
        }

        return fields;
      case SpecificChartType.TimeSeries:
        return {
          name: this.setNameValues(data, chatFrequencyParams, type),
          group_value: chatFrequencyParams.time_interval,
          group_name: chatFrequencyParams.date_field,
          group_path,
          time_value: this.formatTimeSeries(data["value"][0]),
        };
      case SpecificChartType.TimeSeriesCombo:
        return {
          field_path: this.setFieldPath(data, chatFrequencyParams),
          field_name: this.setFieldName(data, chatFrequencyParams),
          name: this.setNameValues(data, chatFrequencyParams, type),
          data_index: data["dataIndex"],
          group_value: "1m",
          group_name: "msg_date_time",
          group_path,
          time_value: this.formatTimeSeries(data["value"][0]),
        };
      case SpecificChartType.ScatterPlot:
        return {
          name: this.setNameValues(data, chatFrequencyParams, type),
        };
      default:
        break;
    }
  }

  setNameValues(params: any, chatFrequencyParams: any, type: any) {
    if (type === SpecificChartType.TimeSeriesCombo) {
      if (
        params["seriesName"] === "Customer Sentiment" ||
        params["seriesName"] === "Cumulative Effort"
      ) {
        return params["value"][1];
      }

      return this.formatTranscriptParams(
        params["name"],
        chatFrequencyParams.date_field,
      );
    }

    if (
      ["sentiment", "cumulative_effort"].includes(
        chatFrequencyParams.field_path,
      ) &&
      params["name"] === ""
    ) {
      return params["value"][1];
    }
    return params["seriesName"];
  }

  setFieldPath(params, chatFrequencyParams) {
    switch (params["seriesName"]) {
      case "Customer Sentiment":
        return "sentiment";
      case "Cumulative Effort":
        return "cumulative_effort";
      default:
        return chatFrequencyParams.field_path;
    }
  }

  setFieldName(params: any, chatFrequencyParams: any) {
    if (params["seriesName"] === "Cumulative Effort") {
      return "cumulative_effort_value";
    } else {
      return chatFrequencyParams.field_name;
    }
  }

  getThemePalette(palleteType): ColorPalleteOptions {
    if (palleteType === PaletteType.CHAT) {
      return ColorThemesChat;
    } else if (palleteType === PaletteType.SURVEY) {
      return ColorThemesSurvey;
    }
    return ColorThemes;
  }

  formatLabelData(chartsFieldsData: ChartsFieldsGroup[]) {
    const labelsCatalog = {};
    const nlpLabels = chartsFieldsData.find(
      (entry) => entry.group_type === "NLP",
    )?.fields;
    const predictorsLabels = chartsFieldsData.find(
      (entry) => entry.group_type === "Models",
    )?.fields;

    nlpLabels?.map((res) => {
      labelsCatalog[res.name] = res.label;
    });

    predictorsLabels?.map((res) => {
      if (res.name === "predict") {
        labelsCatalog[res.path] = res.label;
      }
    });

    return labelsCatalog;
  }

  getDataByName(
    name: string,
    chartsFieldsData: ChartsFieldsGroup[],
    activatedChat: ChatSummary,
  ) {
    const selectedOption = chartsFieldsData
      .find((entry) => entry.fields.find((entry) => entry.label === name))
      .fields.find((entry) => entry.label === name);

    const dataObj = {
      field_name: selectedOption.name,
      field_path: selectedOption.path,
      field_type: selectedOption.type,
      index_id: activatedChat.uuid,
      selected_field: selectedOption.label,
      data_source_type: activatedChat.source_type,
    };

    return dataObj;
  }
}
